import { useEffect } from 'react';
import { Trans, useTranslation } from 'react-i18next';

interface ConfirmDeleteDialogProps {
  /** Conversation title shown in the body (bolded). */
  title: string;
  onCancel: () => void;
  onConfirm: () => void;
}

/**
 * Confirm-delete modal used by both desktop and mobile delete paths.
 * Tap on the scrim, "取消", or Escape cancels; "刪除" confirms.
 */
export function ConfirmDeleteDialog({ title, onCancel, onConfirm }: ConfirmDeleteDialogProps) {
  const { t } = useTranslation();

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onCancel();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onCancel]);

  return (
    <div className="confirm-overlay" onClick={onCancel} role="presentation">
      <div
        className="confirm-dialog"
        onClick={(e) => e.stopPropagation()}
        role="alertdialog"
        aria-modal="true"
        aria-labelledby="confirm-delete-title"
      >
        <div id="confirm-delete-title" className="confirm-title">
          {t('confirmDeleteTitle')}
        </div>
        <div className="confirm-body">
          {/* Trans renders the {{name}} interpolation as a bold <strong> per i18n value. */}
          <Trans
            i18nKey="confirmDeleteBody"
            values={{ name: title }}
            components={{ strong: <strong /> }}
          />
        </div>
        <div className="confirm-actions">
          <button type="button" className="confirm-btn-ghost" onClick={onCancel} autoFocus>
            {t('cancel')}
          </button>
          <button type="button" className="confirm-btn-danger" onClick={onConfirm}>
            {t('delete')}
          </button>
        </div>
      </div>
    </div>
  );
}
